/**
 * R5 落地：统一 REST API 入口
 *
 * 规则：
 * 1. axios 实例 module-private，不直接 export default axios 实例
 * 2. 所有业务模块通过 useApi() 获取实例，禁止 import axios from 'axios'（ESLint 兜底）
 * 3. 请求拦截器：自动注入 Authorization（读 localStorage token）
 * 4. 响应拦截器：统一错误处理 → 401 抛 AuthError / 网络错误统一 reject
 *
 * 白名单说明：本文件是唯一允许直接 import axios 的文件（见 .eslintrc.cjs overrides）
 */

import axios from 'axios'
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import { logger } from '@/utils/logger'
import { getApiBase } from '@/utils/env'
import { resolveMachineCode } from '@/utils/machineCode'
import { generateRequestSignature, isTauri, getDeviceFingerprintHash } from '@/utils/tauri-bridge'
import type { ApiResponse } from '@/types/api'

// ── 内部单例（module-private，不 export） ─────────────────────────────
let _instance: AxiosInstance | null = null

function extractPath(config: InternalAxiosRequestConfig): string {
  const base = (config.baseURL || '').replace(/\/+$/, '')
  const rel = ((config.url || '').split('?')[0] ?? '').replace(/^\/+/, '')
  try {
    return new URL(`${base}/${rel}`).pathname
  } catch {
    const rawUrl = config.url ?? ''
    if (rawUrl.startsWith('http://') || rawUrl.startsWith('https://')) {
      return rawUrl.replace(/\?.*$/, '').replace(/^https?:\/\/[^/]+/, '') || '/'
    }
    const path = rawUrl.split('?')[0] ?? rawUrl
    return path.startsWith('/') ? path : `/${path}`
  }
}

function serializeBody(data: unknown): string {
  if (data === undefined || data === null || data === '') return ''
  return typeof data === 'string' ? data : JSON.stringify(data)
}

/** 创建/获取 axios 单例 */
function getInstance(): AxiosInstance {
  if (_instance) return _instance

  _instance = axios.create({
    baseURL: getApiBase(),
    timeout: 15_000,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  })

  // ── 请求拦截器：注入 Authorization + 机台 KEYT/指纹签名头 ────────────
  // 签名材料与 KioskSignatureMiddleware.php 完全一致：
  //   machineCode|timestamp|nonce|method|path|bodyHash
  _instance.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      // 每次请求同步 baseURL（支持运维运行时修改 localStorage 网络配置）
      config.baseURL = getApiBase()

      // 1. Authorization token
      const token = localStorage.getItem('kiosk_token')
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`
      }

      // 2. Kiosk REST Ed25519 签名（仅 Tauri；私钥不出 JS。瘦 H5 走 ws-ticket）
      try {
        const machineCode = await resolveMachineCode()
        if (machineCode) {
          config.headers['X-Machine-Code'] = machineCode
        }
        if (isTauri() && machineCode) {
          const method = (config.method ?? 'GET').toUpperCase()
          const path = extractPath(config)
          const bodyStr = serializeBody(config.data)
          const signed = await generateRequestSignature(machineCode, method, path, bodyStr)
          if (signed) {
            config.headers['X-Timestamp'] = String(signed.timestamp)
            config.headers['X-Nonce'] = signed.nonce
            config.headers['X-Signature'] = signed.signature
          }
          try {
            const fp = await getDeviceFingerprintHash()
            if (fp) config.headers['X-Device-Fingerprint'] = fp
          } catch (fpErr) {
            logger.warn('[useApi] 设备指纹获取失败', { error: String(fpErr) })
          }
        }
      } catch (signErr) {
        logger.warn('[useApi] 签名生成失败，跳过签名头', { error: String(signErr) })
      }

      return config
    },
    (error: unknown) => {
      logger.error('请求拦截器错误', { error: String(error) })
      return Promise.reject(error instanceof Error ? error : new Error(String(error)))
    },
  )

  // ── 响应拦截器：统一错误处理 ─────────────────────────────────────
  _instance.interceptors.response.use(
    (response: AxiosResponse) => response,
    (error: unknown) => {
      if (axios.isAxiosError(error)) {
        const status = error.response?.status
        const responseData = error.response?.data as any
        
        // 兼容后端多种错误格式：
        // 1. 标准 ApiResponse: { code, message, data: { reject_reason } }
        // 2. 直接错误对象: { error_code, message }
        const rawMessage = responseData?.message || error.message
        const errorCode = responseData?.error_code || responseData?.data?.reject_reason || String(responseData?.code || '')

        // 决定对外展示的消息：优先展示 errorCode 以隐藏后端敏感信息（如数据库表名）
        // 如果 errorCode 是纯数字（通常表示未定义具体业务 code），则降级使用 rawMessage
        const isNumericCode = /^\d+$/.test(errorCode)
        const displayMessage = (errorCode && !isNumericCode) ? errorCode : rawMessage

        if (status === 401) {
          logger.warn('API 鉴权失败 (401)', { url: error.config?.url })
          localStorage.removeItem('kiosk_token')
          return Promise.reject(new ApiError(401, displayMessage || '鉴权失败', errorCode))
        }

        if (status === 403) {
          logger.warn('API 权限不足 (403)', { url: error.config?.url })
          return Promise.reject(new ApiError(403, displayMessage || '无访问权限', errorCode))
        }

        if (status === 404) {
          logger.warn('API 资源不存在 (404)', { url: error.config?.url })
          return Promise.reject(new ApiError(404, displayMessage || '资源不存在', errorCode))
        }

        if (status !== undefined && status >= 500) {
          logger.error('API 服务器错误', { status, url: error.config?.url })
          return Promise.reject(new ApiError(status, displayMessage || '服务器内部错误', errorCode))
        }

        if (error.response === undefined) {
          logger.warn('网络连接失败', { url: error.config?.url })
          return Promise.reject(new ApiError(0, '网络连接失败，请检查网络'))
        }

        return Promise.reject(new ApiError(status ?? 0, displayMessage, errorCode))
      }

      logger.error('未知请求错误', { error: String(error) })
      return Promise.reject(error instanceof Error ? error : new Error(String(error)))
    },
  )

  return _instance
}

// ── 自定义错误类型 ───────────────────────────────────────────────────
export class ApiError extends Error {
  public readonly statusCode: number
  public readonly errorCode?: string

  constructor(statusCode: number, message: string, errorCode?: string) {
    super(message)
    this.name = 'ApiError'
    this.statusCode = statusCode
    this.errorCode = errorCode
  }
}

// ── 公共 composable（供所有业务模块使用） ────────────────────────────
export function useApi() {
  const instance = getInstance()

  return {
    /** GET 请求 */
    async get<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
      const res = await instance.get<ApiResponse<T>>(url, config)
      return res.data
    },

    /** POST 请求 */
    async post<T = unknown>(
      url: string,
      data?: unknown,
      config?: AxiosRequestConfig,
    ): Promise<ApiResponse<T>> {
      const res = await instance.post<ApiResponse<T>>(url, data, config)
      return res.data
    },

    /** PUT 请求 */
    async put<T = unknown>(
      url: string,
      data?: unknown,
      config?: AxiosRequestConfig,
    ): Promise<ApiResponse<T>> {
      const res = await instance.put<ApiResponse<T>>(url, data, config)
      return res.data
    },

    /** DELETE 请求 */
    async delete<T = unknown>(
      url: string,
      config?: AxiosRequestConfig,
    ): Promise<ApiResponse<T>> {
      const res = await instance.delete<ApiResponse<T>>(url, config)
      return res.data
    },

    /** 获取原始 axios 实例（高级用法，如文件上传） */
    raw: instance,
  }
}

/**
 * 顶层 api 实例（非 composable 上下文可用）
 * 注意：仍走同一单例，规则与 useApi() 相同
 */
export const api = {
  get: <T = unknown>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> =>
    useApi().get<T>(url, config),
  post: <T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<ApiResponse<T>> => useApi().post<T>(url, data, config),
  put: <T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<ApiResponse<T>> => useApi().put<T>(url, data, config),
  delete: <T = unknown>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> =>
    useApi().delete<T>(url, config),
}

/** 重置单例（网络配置变更 / 单测） */
export function resetApiClient(): void {
  _instance = null
}

/** @internal */
export function _resetInstanceForTest(): void {
  resetApiClient()
}
